"use client";

import "helpers/reactUseSyncExternalStoreShim";
import { useEffect } from "react";
import { create } from "zustand";

import { type Vendor, type VendorIds, VENDOR_IDS } from "./vendors";

export const isDefined = <T>(value: T | undefined | null): value is T => {
    return value !== undefined && value !== null;
};

export type VendorConsents = Record<Vendor, boolean>;

export type VendorConsentStore = {
    vendorConsents?: VendorConsents;
    setVendorConsents: (value: VendorConsents) => void;
    isConsentInteractionFinished: boolean;
    setConsentInteractionFinished: (value: boolean) => void;
};

export const useVendorConsentStore = create<VendorConsentStore>((set) => ({
    vendorConsents: undefined,
    setVendorConsents: (value: VendorConsents) => {
        set({ vendorConsents: value });
    },
    isConsentInteractionFinished: false,
    setConsentInteractionFinished: (value: boolean) => {
        set({ isConsentInteractionFinished: value });
    }
}));

export const checkConsentByVendorIds = (vendorIds: VendorIds): Promise<boolean> => {
    return new Promise((resolve) => {
        window.__tcfapi(
            "checkConsent",
            2,
            (data) => {
                const vendorData = data as { hasConsent: boolean }[];
                resolve(vendorData.some((vendor) => vendor.hasConsent));
            },
            { data: vendorIds.map((vendorId) => ({ vendorId })) }
        );
    });
};

export const checkVendorConsents = async () => {
    const consents: VendorConsents = Object.fromEntries(
        await Promise.all(
            Object.entries(VENDOR_IDS).map(async ([vendor, ids]) => [
                vendor,
                await checkConsentByVendorIds(ids)
            ])
        )
    );

    useVendorConsentStore.getState().setVendorConsents(consents);
};

export const useConsent = () => {
    const { setConsentInteractionFinished, vendorConsents, isConsentInteractionFinished } =
        useVendorConsentStore();
    useEffect(() => {
        if (!isDefined(window.__tcfapi)) {
            return;
        }

        window.__tcfapi("addEventListener", 2, (tcData, success) => {
            if (
                success &&
                (tcData.eventStatus === "tcloaded" || tcData.eventStatus === "useractioncomplete")
            ) {
                window.__tcfapi("removeEventListener", 2, () => undefined, tcData.listenerId);
                setConsentInteractionFinished(true);
                checkVendorConsents();
            }
        });
    }, [setConsentInteractionFinished]);

    const checkVendorConsent = (vendorName: Vendor) =>
        isConsentInteractionFinished && !!vendorConsents?.[vendorName];

    return { vendorConsents, isConsentInteractionFinished, checkVendorConsent };
};
